import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import * as Zod from 'zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { ConfirmDialog } from 'common/components/ConfirmDialog';
import {
    IOfferEventDetailFragment,
    IOfferEventType,
    OfferEventUnenrollCurrentUserDocument,
} from 'graphql/types';
import { formatDate } from 'common/utils/formatDate';
import { useApolloError } from 'common/hooks/useApolloError';
import { SelectField, TextField } from 'common/forms/Field';

type TFormValues = {
    reason: string;
    otherReason?: string;
};

interface IProps {
    offerEvent: IOfferEventDetailFragment;
    open?: boolean;
    onCancel?: () => void;
    onClose: () => void;
}

export const OfferEventUnsubscribeDialog = ({
    offerEvent,
    open,
    onCancel,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [unenrollCurrentUser, { loading }] = useMutation(
        OfferEventUnenrollCurrentUserDocument
    );

    const { closingDate, type } = offerEvent;

    const isWebinar = type === IOfferEventType.Webinar;
    const isMeeting = type === IOfferEventType.Meeting;
    const isWebinarOrMeeting = isWebinar || isMeeting;

    const isAccessContent =
        type === IOfferEventType.Training ||
        type === IOfferEventType.Elearning ||
        type === IOfferEventType.Course;

    let contentText;
    let confirmText;
    const unsubscribeDialogTransKey = 'offerEventUnsubscribeDialog';

    if (isWebinarOrMeeting) {
        contentText = translate(`${unsubscribeDialogTransKey}.text`);

        if (!!closingDate) {
            contentText = `${contentText} ${translate(
                `${unsubscribeDialogTransKey}.textAddition`,
                {
                    date: formatDate(closingDate, 'dd-MM-yyyy'),
                }
            )}`;
        } else {
            contentText = `${contentText} ${translate(
                `${unsubscribeDialogTransKey}.textAdditionNoDate`
            )}`;
        }

        confirmText = translate(
            `${unsubscribeDialogTransKey}.buttonTextMeeting`
        );
    } else {
        contentText = translate(
            `${unsubscribeDialogTransKey}.${isAccessContent ? 'textAccess' : 'text'}`
        );
        confirmText = translate(
            `${unsubscribeDialogTransKey}.${
                isAccessContent ? 'buttonTextAccess' : 'buttonText'
            }`
        );
    }

    const handleUnsubscribe: SubmitHandler<TFormValues> = async (
        values?: TFormValues
    ) => {
        if (!offerEvent) return;

        try {
            const { reason: reasonValue, otherReason } = values || {};

            const reason =
                reasonValue === reason4Translation ? otherReason : reasonValue;

            await unenrollCurrentUser({
                variables: {
                    offerEventId: offerEvent.id,
                    reason,
                },
                update: (cache) => {
                    cache.evict({
                        id: `OfferEvent:${offerEvent.id}`,
                        broadcast: true,
                    });
                    cache.evict({ fieldName: 'offerEvents' });
                    cache.evict({ fieldName: 'paginatedOfferEvents' });

                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        onClose?.();
    };

    const reason1Translation = translate(
        `${unsubscribeDialogTransKey}.reason1`
    );
    const reason2Translation = translate(
        `${unsubscribeDialogTransKey}.reason2`
    );
    const reason3Translation = translate(
        `${unsubscribeDialogTransKey}.reason3`
    );
    const reason4Translation = translate(
        `${unsubscribeDialogTransKey}.reason4`
    );

    const validationSchema = Zod.object({
        reason: Zod.string(),
        otherReason: Zod.string().optional(),
    }).refine(
        (data) => {
            // Only if reason is reason4, otherReason is required
            if (data.reason === reason4Translation) {
                return !!data.otherReason?.trim();
            }

            return true;
        },
        {
            message: translate('validate.required'),
            path: ['otherReason'],
        }
    );

    const { control, handleSubmit, watch } = useForm<TFormValues>({
        defaultValues: {
            reason: isWebinarOrMeeting ? reason1Translation : undefined,
        },
        resolver: isWebinarOrMeeting
            ? zodResolver(validationSchema)
            : undefined,
    });

    const reasonOptions = [
        {
            label: reason1Translation,
            value: reason1Translation,
        },
        {
            label: reason2Translation,
            value: reason2Translation,
        },
        {
            label: reason3Translation,
            value: reason3Translation,
        },
        {
            label: reason4Translation,
            value: reason4Translation,
        },
    ];

    if (!isWebinarOrMeeting) {
        return (
            <ConfirmDialog
                confirmText={confirmText}
                loading={loading}
                open={!!open}
                title={translate(
                    `${unsubscribeDialogTransKey}.${
                        isAccessContent ? 'titleAccess' : 'title'
                    }`
                )}
                onCancel={onCancel}
                onClose={onClose}
                onConfirm={handleSubmit(handleUnsubscribe)}
            >
                {contentText}
            </ConfirmDialog>
        );
    }

    const reasonValue = watch('reason');

    return (
        <ConfirmDialog
            confirmText={confirmText}
            loading={loading}
            open={!!open}
            title={translate(
                `${unsubscribeDialogTransKey}.${
                    isAccessContent ? 'titleAccess' : 'title'
                }`
            )}
            onCancel={onCancel}
            onClose={onClose}
            onConfirm={handleSubmit(handleUnsubscribe)}
        >
            <form>
                {contentText}

                <SelectField
                    control={control}
                    name="reason"
                    options={reasonOptions}
                />

                {reasonValue === reason4Translation && (
                    <TextField multiline control={control} name="otherReason" />
                )}
            </form>
        </ConfirmDialog>
    );
};
